import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Package, User, ShieldCheck, Tag, Boxes, CalendarClock, AlertCircle } from 'lucide-react'
import { getPublicAsset, type PublicAsset } from '@/api/inventory'

const STATUS_STYLES: Record<string, string> = {
  available: 'bg-green-100 text-green-700',
  assigned: 'bg-blue-100 text-blue-700',
  repair: 'bg-amber-100 text-amber-700',
  retired: 'bg-gray-200 text-gray-600',
}

function fmtDate(d?: string | null): string {
  if (!d) return '—'
  const dt = new Date(d)
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export default function PublicAssetPage() {
  const { orgSlug, assetTag } = useParams<{ orgSlug: string; assetTag: string }>()
  const { data, isLoading, isError } = useQuery<PublicAsset>({
    queryKey: ['public-asset', orgSlug, assetTag],
    queryFn: () => getPublicAsset(orgSlug!, assetTag!),
    enabled: !!orgSlug && !!assetTag,
    retry: false,
  })

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-start sm:items-center justify-center p-4">
      <div className="w-full max-w-md">
        {isLoading ? (
          <Card><p className="text-center text-gray-400 py-10">Loading…</p></Card>
        ) : isError || !data ? (
          <Card>
            <div className="text-center py-10">
              <AlertCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <h1 className="font-semibold text-gray-900">Asset not found</h1>
              <p className="text-sm text-gray-500 mt-1">This asset tag doesn’t match any item{orgSlug ? ` for ${orgSlug}` : ''}.</p>
            </div>
          </Card>
        ) : (
          <>
            <div className="text-center mb-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                <Package className="h-3.5 w-3.5 text-[#EC5B2C]" /> {data.org_name} · Asset
              </span>
            </div>
            <Card>
              {data.image_url ? (
                <img src={data.image_url} alt={data.name} className="w-full h-44 object-contain rounded-lg bg-gray-50 mb-4" />
              ) : (
                <div className="w-full h-44 rounded-lg bg-gray-50 mb-4 flex items-center justify-center">
                  <Package className="h-14 w-14 text-gray-300" />
                </div>
              )}

              <div className="flex items-start justify-between gap-3 mb-4">
                <h1 className="text-xl font-bold text-gray-900 leading-tight">{data.name}</h1>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium capitalize ${STATUS_STYLES[data.status] || 'bg-gray-100 text-gray-600'}`}>{data.status}</span>
              </div>

              <dl className="divide-y divide-gray-100">
                <Row icon={<Tag className="h-4 w-4" />} label="Asset Tag" value={data.asset_tag || '—'} mono />
                <Row icon={<Boxes className="h-4 w-4" />} label="Category" value={data.category} capitalize />
                {data.condition && <Row icon={<ShieldCheck className="h-4 w-4" />} label="Condition" value={data.condition} capitalize />}
                {data.quantity > 1 && <Row icon={<Boxes className="h-4 w-4" />} label="Quantity" value={String(data.quantity)} />}
                <Row icon={<User className="h-4 w-4" />} label="Assigned to" value={data.current_holder_name || 'Unassigned'} />
                {data.assigned_since && <Row icon={<CalendarClock className="h-4 w-4" />} label="Since" value={fmtDate(data.assigned_since)} />}
                {data.warranty_expiry && <Row icon={<ShieldCheck className="h-4 w-4" />} label="Warranty until" value={fmtDate(data.warranty_expiry)} />}
              </dl>
            </Card>
            <p className="text-center text-xs text-gray-400 mt-4">Scanned from an asset label · {data.org_name}</p>
          </>
        )}
      </div>
    </div>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">{children}</div>
}

function Row({ icon, label, value, mono, capitalize }: { icon: React.ReactNode; label: string; value: string; mono?: boolean; capitalize?: boolean }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="flex items-center gap-2 text-sm text-gray-500">{icon}{label}</span>
      <span className={`text-sm font-medium text-gray-900 text-right ${mono ? 'font-mono' : ''} ${capitalize ? 'capitalize' : ''}`}>{value}</span>
    </div>
  )
}
